import { useProcessExample } from "@via/pages/projects/pipelines/pipelines.api";
import { formatDateString } from "@via/utils/helpers.tsx";
import { JSX } from "react";

export type ProcessExample = {
  capturedAt: string;
  manifest: string;
  reportHtml: string;
  generatorVersion?: string;
};

/**
 * Shows the example captured for this revision: what the process produced on one accepted run.
 *
 * ⚠️ The wording never claims the output is correct. An example is a record of one run, and the
 * report itself says so; nothing here should add a stronger claim on top of it.
 *
 * Fetches through the same query key the parent form's tab gate uses, so react-query serves both
 * from one request rather than issuing a second.
 */
export const ExampleTab = ({
  processId,
}: {
  processId?: string;
}): JSX.Element => {
  const { data: example, isLoading } = useProcessExample(processId ?? "");

  if (isLoading) {
    return (
      <div className="p-4 text-sm text-gray-500">Loading the example…</div>
    );
  }

  if (!example) {
    // The parent gates this tab on the example existing, so this is the narrow window where a
    // capture was superseded by an edit while the tab was open.
    return (
      <div className="p-4 text-sm text-gray-500">
        This revision has no example. Capture an accepted test run to record
        what it produced.
      </div>
    );
  }

  const typed = example as ProcessExample;

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="text-sm text-gray-600">
        <span className="font-semibold">Captured</span>{" "}
        {formatDateString(typed.capturedAt)}
        {typed.generatorVersion ? (
          <span className="ml-2 text-gray-400">({typed.generatorVersion})</span>
        ) : null}
      </div>
      <p className="max-w-3xl text-sm text-gray-500">
        These are the files this process produced on one run. They are a record
        of that run, not a statement that the output is right for any other
        input.
      </p>
      {/*
        srcDoc in a sandboxed iframe is defence in depth on top of generating the markup ourselves.
        The report already carries no script and no external reference; an empty sandbox attribute
        withholds every permission, so a future regression in the generator still could not reach
        the app's origin.
      */}
      <iframe
        title="Example output"
        sandbox=""
        srcDoc={typed.reportHtml}
        style={{ width: "100%", height: "70vh", border: "1px solid #ddd" }}
      />
      <details>
        <summary className="cursor-pointer text-sm text-gray-600">
          example-manifest.yaml
        </summary>
        <pre className="mt-2 overflow-x-auto rounded border border-gray-200 bg-gray-50 p-3 text-xs">
          {typed.manifest}
        </pre>
      </details>
    </div>
  );
};
